import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import type { ComponentProps } from 'react';
import { cn } from '@/utils/cn';

/**
 * Tooltip over the Radix primitive, which is what makes it reachable rather than mouse-only: the
 * content opens on keyboard focus as well as hover, closes on Escape, and can be hovered without
 * vanishing — the three things WCAG 2.1 AA 1.4.13 asks for.
 *
 * Each part is declared as a function component rather than re-exported as a const, so this file
 * exports components only and Vite's fast refresh keeps working (the same reason `buttonVariants`
 * lives outside `button.tsx`).
 */
export function TooltipProvider(
  props: ComponentProps<typeof TooltipPrimitive.Provider>,
): React.JSX.Element {
  return <TooltipPrimitive.Provider {...props} />;
}

export function Tooltip(props: ComponentProps<typeof TooltipPrimitive.Root>): React.JSX.Element {
  return <TooltipPrimitive.Root {...props} />;
}

export function TooltipTrigger(
  props: ComponentProps<typeof TooltipPrimitive.Trigger>,
): React.JSX.Element {
  return <TooltipPrimitive.Trigger {...props} />;
}

export function TooltipContent({
  className,
  sideOffset = 6,
  children,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Content>): React.JSX.Element {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        sideOffset={sideOffset}
        className={cn(
          'bg-brand-surface z-50 max-w-xs rounded-md px-3 py-2 text-xs leading-relaxed text-on-brand shadow-md',
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="fill-brand-600" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}
